import { Modal } from 'ui/dialog/modal/modal';
import 'ui/form-group/form-group';
import h from 'hyperscript';
import { LayerProperty } from 'cadenza/map/layer-property';
import { selectOtherLayerApi } from 'cadenza/api-client/select-other-layer-api';
import { context } from 'cadenza/map/context';
import { logger } from 'cadenza/utils/logging';
import { hasAttributeTable, refreshLayer } from 'cadenza/map/layer-utils';
import { MAP } from 'cadenza/map-dashboard/event-types';
import { MapsApi } from 'cadenza/api-client/maps-api';
import { MapProperty } from 'cadenza/map/map-property';

import i18n from './select-from-other-layer.properties';

export class SelectFromOtherLayerDialog extends Modal {

  #map;

  constructor (map, sourceLayerId) {
    super({ title: i18n('selectionFromAnotherLayer.title'), size: 's' });
    this.#map = map;
    this.form.noValidate = true;
    this.body.append(
      h('form-group', [
        h('label', i18n('selectionFromAnotherLayer.targetLayerLabel')),
        h('select.form-control.targetLayer', {
          required: true,
          name: 'targetLayer'
        },
        this._findIntersectableLayers().map(
          layer => h('option', {
            value: layer.get(LayerProperty.ID),
            selected: layer.get(LayerProperty.ID) !== sourceLayerId
          }, layer.get(LayerProperty.NAME))
        ))
      ]),
      h('form-group', [
        h('label', i18n('selectionFromAnotherLayer.sourceLayerLabel')),
        h('select.form-control.sourceLayer', {
          required: true,
          name: 'sourceLayer'
        },
        this._findIntersectableLayers().map(
          layer => h('option', {
            value: layer.get(LayerProperty.ID),
            selected: layer.get(LayerProperty.ID) === sourceLayerId
          }, layer.get(LayerProperty.NAME))
        ))
      ]),
      h('form-group', [
        h('label', i18n('selectionFromAnotherLayer.distance')),
        h('input.form-control', {
          type: 'number',
          name: 'distance',
          min: 0,
          max: 100000000,
          step: 0.1,
          required: true
        })
      ]));

    this.addSubmitButton();
    this.addCancelButton();
  }

  onSubmit () {
    const targetLayerId = this.form.targetLayer.value;
    const sourceLayerId = this.form.sourceLayer.value;
    const distance = this.form.distance;
    const distanceValid = distance.closest('form-group').checkValidity();
    if (distanceValid) {
      selectOtherLayerApi({
        sourceLayerId,
        targetLayerId,
        distance: distance.valueAsNumber,
        mapSrs: this.#map.getView().getProjection().getCode(),
        mapScale: context(this.#map).mapScale
      })
        .then(() => {
          this._redrawAllLayers();
          this._triggerSelectObjectsEvent(targetLayerId);
          this.close();
        })
        .catch(error => {
          logger.error('Failed to create geometry buffer', error);
          if (error.status === 500) { this.setAlert(i18n('error.server.message')); }
        });
    }
  }

  _findIntersectableLayers () {
    return this.#map.getLayers().getArray().filter(layer => layer.get(LayerProperty.IS_INTERSECTABLE));
  }

  _redrawAllLayers () {
    this.#map.getLayers().forEach(refreshLayer);
  }

  _triggerSelectObjectsEvent (layerId) {
    const layer = context(this.#map).getLayer(layerId);
    layer.dispatchEvent('propertychange');
    const mapScale = context(this.#map).mapScale;
    return new MapsApi({ mapId: this.#map.get(MapProperty.ID) }).getSelectedLayerFeaturesIds(layerId, mapScale)
      .then(selectedFeatures => {
        if (selectedFeatures) {
          layer.set(LayerProperty.SELECTED_FEATURE_IDS, selectedFeatures.selectedFeatureIDs);
          if (hasAttributeTable(layer)) {
            const selectionMap = {};
            selectionMap[layer.get(LayerProperty.ID)] = selectedFeatures.selectedFeatureIDs;
            this.#map.getTargetElement().dispatchEvent(new CustomEvent(MAP.SELECTOR_LAYER_SELECTION_CHANGED, { detail: selectionMap }));
          }
        }
      })
      .catch(err => { logger.error('Cannot select features', err); });
  }

}

customElements.define('d-selection-from-another-layer-dialog', SelectFromOtherLayerDialog);
